import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Hotel, Loader2, Upload, Download, RefreshCw, AlertTriangle, Link as LinkIcon, Copy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  awaiting_payment: 'Wartet auf Zahlung',
  paid: 'Bezahlt – buchen',
  booked: 'Gebucht',
  delivered: 'Geliefert',
  cancelled: 'Storniert',
  completed: 'Erledigt (behalten)',
  refused: 'Abgelehnt',
};

const STATUS_STYLES = {
  awaiting_payment: 'bg-amber-100 text-amber-800',
  paid: 'bg-blue-100 text-blue-800',
  booked: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-600',
  completed: 'bg-gray-100 text-gray-600',
  refused: 'bg-red-100 text-red-700',
};

const FILTERS = [
  { value: 'open', label: 'Offen' },
  { value: 'all', label: 'Alle' },
  { value: 'closed', label: 'Abgeschlossen' },
];

const OPEN_STATUSES = ['awaiting_payment', 'paid', 'booked', 'delivered'];

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('de-DE') : '—');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—');

/** ISO-String -> Wert für <input type="datetime-local"> in lokaler Zeit. */
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const hoursUntil = (iso) => (iso ? (new Date(iso).getTime() - Date.now()) / 36e5 : null);

/**
 * Admin-Tab "Hotel": Warteschlange der Hotel-Reservierungen mit Statuswechsel,
 * PDF-Upload, Storno-Frist und DHMAD-Link für das Hotel-Produkt.
 */
const AdminHotelBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState({ hotel_name: '', booking_reference: '', cancellation_deadline: '', admin_note: '' });
  const [saving, setSaving] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dhmadUrl, setDhmadUrl] = useState('');
  const [savingDhmad, setSavingDhmad] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/hotel-bookings`);
      setBookings(res.data.bookings || []);
    } catch {
      toast.error('Hotel-Bestellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
    axios.get(`${API}/payment/dhmad-hotel-link`).then((res) => setDhmadUrl(res.data?.url || '')).catch(() => {});
  }, [fetchBookings]);

  const visible = useMemo(() => {
    if (filter === 'open') return bookings.filter((b) => OPEN_STATUSES.includes(b.status));
    if (filter === 'closed') return bookings.filter((b) => !OPEN_STATUSES.includes(b.status));
    return bookings;
  }, [bookings, filter]);

  const counts = useMemo(() => ({
    toPay: bookings.filter((b) => b.status === 'awaiting_payment').length,
    toBook: bookings.filter((b) => b.status === 'paid').length,
    toCancelSoon: bookings.filter((b) => ['booked', 'delivered'].includes(b.status)
      && hoursUntil(b.cancellation_deadline) !== null && hoursUntil(b.cancellation_deadline) < 48).length,
  }), [bookings]);

  const openDialog = (b) => {
    setSelected(b);
    setPdfFile(null);
    setEdit({
      hotel_name: b.hotel_name || '',
      booking_reference: b.booking_reference || '',
      cancellation_deadline: toLocalInput(b.cancellation_deadline),
      admin_note: b.admin_note || '',
    });
  };

  const replaceBooking = (updated) => {
    setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
    setSelected(updated);
  };

  const updateStatus = async (status) => {
    if (!selected) return;
    if (['cancelled', 'completed', 'refused'].includes(status)
      // eslint-disable-next-line no-alert
      && !window.confirm('Bestellung abschließen? Namen der Reisenden und das PDF werden dabei gelöscht.')) {
      return;
    }
    setSaving(status);
    try {
      const res = await axios.patch(`${API}/admin/hotel-bookings/${selected.id}`, {
        status,
        hotel_name: edit.hotel_name,
        booking_reference: edit.booking_reference,
        cancellation_deadline: edit.cancellation_deadline ? new Date(edit.cancellation_deadline).toISOString() : null,
        admin_note: edit.admin_note,
      });
      replaceBooking(res.data.booking);
      toast.success(`Status: ${STATUS_LABELS[status]}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(null);
    }
  };

  const uploadPdf = async () => {
    if (!selected || !pdfFile) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', pdfFile);
      await axios.post(`${API}/admin/hotel-bookings/${selected.id}/pdf`, data);
      replaceBooking({ ...selected, has_pdf: true, pdf_filename: `${selected.id}.pdf` });
      setPdfFile(null);
      toast.success('PDF hochgeladen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const downloadPdf = async (b) => {
    try {
      const res = await axios.get(`${API}/hotel-bookings/${b.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('PDF nicht verfügbar');
    }
  };

  const saveDhmadUrl = async () => {
    setSavingDhmad(true);
    try {
      await axios.post(`${API}/admin/payment/dhmad-hotel-link`, { url: dhmadUrl });
      toast.success('DHMAD-Link für Hotel gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSavingDhmad(false);
    }
  };

  const copyTravelers = (b) => {
    const text = (b.travelers || []).map((t) => `${t.first_name} ${t.last_name}`).join('\n');
    navigator.clipboard?.writeText(text).then(() => toast.success('Namen kopiert')).catch(() => {});
  };

  return (
    <div className="space-y-4">
      {/* Kennzahlen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="rounded-sm border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Wartet auf Zahlung (DHMAD prüfen)</p>
            <p className="text-2xl font-bold text-amber-600">{counts.toPay}</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Bezahlt – jetzt buchen</p>
            <p className="text-2xl font-bold text-blue-600">{counts.toBook}</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Storno-Frist in &lt; 48 h</p>
            <p className={`text-2xl font-bold ${counts.toCancelSoon > 0 ? 'text-red-600' : 'text-gray-400'}`}>{counts.toCancelSoon}</p>
          </CardContent>
        </Card>
      </div>

      {/* DHMAD-Link fürs Hotel-Produkt (20 TND) */}
      <Card className="rounded-sm border-0 shadow-sm">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1 space-y-2">
            <Label htmlFor="dhmad-hotel-url" className="flex items-center gap-1">
              <LinkIcon className="w-4 h-4" /> DHMAD-Zahlungslink Hotel (20 TND)
            </Label>
            <Input id="dhmad-hotel-url" value={dhmadUrl} onChange={(e) => setDhmadUrl(e.target.value)} placeholder="https://dhmad.tn/..." />
          </div>
          <Button className="rounded-sm" onClick={saveDhmadUrl} disabled={savingDhmad}>
            {savingDhmad && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Speichern
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="font-heading flex items-center gap-2">
            <Hotel className="w-5 h-5" /> Hotel-Reservierungen
          </CardTitle>
          <div className="flex items-center gap-2">
            {FILTERS.map((f) => (
              <Button key={f.value} size="sm" variant={filter === f.value ? 'default' : 'outline'} className="rounded-sm"
                onClick={() => setFilter(f.value)}>
                {f.label}
              </Button>
            ))}
            <Button size="icon" variant="ghost" onClick={fetchBookings} aria-label="Neu laden">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {visible.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">{loading ? 'Lädt…' : 'Keine Bestellungen.'}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Erstellt</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Ziel</TableHead>
                  <TableHead>Aufenthalt</TableHead>
                  <TableHead>Visa-Termin</TableHead>
                  <TableHead>Zahlung</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Storno-Frist</TableHead>
                  <TableHead>Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((b) => {
                  const h = hoursUntil(b.cancellation_deadline);
                  const urgent = ['booked', 'delivered'].includes(b.status) && h !== null && h < 48;
                  return (
                    <TableRow key={b.id} className={urgent ? 'bg-red-50' : ''}>
                      <TableCell className="whitespace-nowrap">{fmtDate(b.created_at)}</TableCell>
                      <TableCell>
                        <div className="font-medium">{b.user_name}</div>
                        <div className="text-xs text-gray-500">{b.user_email}</div>
                      </TableCell>
                      <TableCell>{b.city}, {b.destination_country}</TableCell>
                      <TableCell className="whitespace-nowrap">{fmtDate(b.check_in)} → {fmtDate(b.check_out)}</TableCell>
                      <TableCell className="whitespace-nowrap">{fmtDate(b.appointment_date)}</TableCell>
                      <TableCell>{b.payment_method === 'stripe' ? 'Stripe 15 €' : 'DHMAD 20 TND'}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded-sm whitespace-nowrap ${STATUS_STYLES[b.status]}`}>
                          {STATUS_LABELS[b.status] || b.status}
                        </span>
                      </TableCell>
                      <TableCell className={`whitespace-nowrap ${urgent ? 'text-red-700 font-semibold' : ''}`}>
                        {urgent && <AlertTriangle className="w-4 h-4 inline mr-1" />}
                        {fmtDateTime(b.cancellation_deadline)}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" className="rounded-sm" onClick={() => openDialog(b)}>
                          Bearbeiten
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail-Dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Hotel className="w-5 h-5" />
                  {selected.city}, {selected.destination_country}
                  <span className={`text-xs px-2 py-1 rounded-sm ${STATUS_STYLES[selected.status]}`}>
                    {STATUS_LABELS[selected.status]}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  Bestellung {selected.id.slice(0, 8)} · {selected.user_name} ({selected.user_email})
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-gray-500">Aufenthalt:</span> {fmtDate(selected.check_in)} → {fmtDate(selected.check_out)}</div>
                  <div><span className="text-gray-500">Visa-Termin:</span> {fmtDate(selected.appointment_date)}</div>
                  <div><span className="text-gray-500">Zahlung:</span> {selected.payment_method === 'stripe' ? 'Stripe 15 €' : 'DHMAD 20 TND'}</div>
                  <div><span className="text-gray-500">WhatsApp:</span> {selected.whatsapp || '—'}</div>
                </div>

                {selected.travelers?.length > 0 && (
                  <div className="rounded-sm border p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">Reisende (wie im Pass)</span>
                      <Button size="sm" variant="ghost" onClick={() => copyTravelers(selected)}>
                        <Copy className="w-4 h-4 mr-1" /> Kopieren
                      </Button>
                    </div>
                    <ul className="list-disc pl-5">
                      {selected.travelers.map((t, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={i}>{t.first_name} {t.last_name}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {selected.notes && (
                  <div className="rounded-sm bg-gray-50 p-3"><span className="text-gray-500">Kundenhinweis:</span> {selected.notes}</div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Hotelname</Label>
                    <Input value={edit.hotel_name} onChange={(e) => setEdit({ ...edit, hotel_name: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>Buchungsnummer</Label>
                    <Input value={edit.booking_reference} onChange={(e) => setEdit({ ...edit, booking_reference: e.target.value })} />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label>Kostenlose Stornierung bis (Pflicht bei „Gebucht“)</Label>
                    <Input type="datetime-local" value={edit.cancellation_deadline}
                      onChange={(e) => setEdit({ ...edit, cancellation_deadline: e.target.value })} />
                    <p className="text-xs text-gray-500">Muss nach dem Visa-Termin liegen. Erinnerung kommt 48 h vorher per E-Mail/Telegram.</p>
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label>Interne Notiz</Label>
                    <Textarea rows={2} value={edit.admin_note} onChange={(e) => setEdit({ ...edit, admin_note: e.target.value })} />
                  </div>
                </div>

                {/* PDF */}
                {OPEN_STATUSES.includes(selected.status) && (
                  <div className="rounded-sm border p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Bestätigungs-PDF</span>
                      {selected.has_pdf && (
                        <Button size="sm" variant="ghost" onClick={() => downloadPdf(selected)}>
                          <Download className="w-4 h-4 mr-1" /> Ansehen
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} />
                      <Button size="sm" className="rounded-sm" onClick={uploadPdf} disabled={!pdfFile || uploading}>
                        {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        Hochladen
                      </Button>
                    </div>
                    {!selected.has_pdf && <p className="text-xs text-gray-500">Noch kein PDF hochgeladen.</p>}
                  </div>
                )}
              </div>

              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                {selected.status === 'awaiting_payment' && (
                  <Button className="rounded-sm bg-blue-600 hover:bg-blue-700" disabled={!!saving} onClick={() => updateStatus('paid')}>
                    {saving === 'paid' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Zahlung bestätigt
                  </Button>
                )}
                {selected.status === 'paid' && (
                  <Button className="rounded-sm bg-indigo-600 hover:bg-indigo-700" disabled={!!saving} onClick={() => updateStatus('booked')}>
                    {saving === 'booked' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Als gebucht markieren
                  </Button>
                )}
                {['paid', 'booked'].includes(selected.status) && (
                  <Button className="rounded-sm bg-green-600 hover:bg-green-700" disabled={!!saving || !selected.has_pdf}
                    onClick={() => updateStatus('delivered')}>
                    {saving === 'delivered' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    An Kunden liefern
                  </Button>
                )}
                {['booked', 'delivered'].includes(selected.status) && (
                  <Button variant="outline" className="rounded-sm" disabled={!!saving} onClick={() => updateStatus(selected.status)}>
                    Daten speichern
                  </Button>
                )}
                {['booked', 'delivered'].includes(selected.status) && (
                  <Button variant="outline" className="rounded-sm border-red-300 text-red-700" disabled={!!saving}
                    onClick={() => updateStatus('cancelled')}>
                    Hotel storniert
                  </Button>
                )}
                {selected.status === 'delivered' && (
                  <Button variant="outline" className="rounded-sm" disabled={!!saving} onClick={() => updateStatus('completed')}>
                    Kunde behält Buchung
                  </Button>
                )}
                {OPEN_STATUSES.includes(selected.status) && (
                  <Button variant="ghost" className="rounded-sm text-red-600" disabled={!!saving} onClick={() => updateStatus('refused')}>
                    Ablehnen
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHotelBookings;
